import { parseRequiredRightElementAttribute, parseRightElementAttribute } from "UI/UI/Rights/Utils";
import { DataConverter } from "../Converters/DataConverter";

/**
 * Typy práv aplikovatelné na komponenty - vyjádřené jako flagy
 */
export enum RightType {
    read = 1 << 0, //TODO-rites: bude potřeba??
    insert = 1 << 1,
    edit = 1 << 2,
    delete = 1 << 3,
    visible = 1 << 4,
    enabled = 1 << 5,
    required = 1 << 6,
    postponedRequired = 1 << 7,
    recommended = 1 << 8,
    closeForm = 1 << 9,
    validateIf = 1 << 10,
    confirmIf = 1 << 11,
    useDigitalSign = 1 << 12,
    useEncryption = 1 << 13,
    useDecryption = 1 << 14,
    openAsEnabled = 1 << 15,
    editing = 1 << 16,
    interactive = 1 << 17,
    enabledVisual = 1 << 18,
    multiEditing = 1 << 19,
    editableComponentRights = enabled | visible | openAsEnabled | required | postponedRequired | recommended,
    actionRights = enabled | visible | openAsEnabled,
}

/**
 * Úroveň, na které je definováno právo
 * Stejný výčet jako RightLevel na serveru
 */
export enum RightLevel {
    classAlgorithmicLevel, //Level=0. Práva z DB pro třídu, a má algoritmickou úroveň = true (Algoritmic)
    classInterfaceLevel, //Level=1. Práva z DB pro třídu, a má algoritmickou úroveň = false nebo null (Interface)
    formDefinitionLevel, //Level=2. Práva z definice formuláře
    formLevel, //Level=3. Práva z DB pro formulář
    allRolesFormLevel, //Level=4. Práva z DB pro formulář, kde RoleID=0. Pro zpracování takových práv prvků z DB na vlastní úrovni
    allRolesClassAlgorithmicLevel, //Práva z DB pro třídu pro algoritmickou úroveň, kde RoleID=0
    allRolesClassInterfaceLevel, //Práva z DB pro třídu pro úroveň interface, kde RoleID=0
    allRolesSystemOperationLevel, //Práva z DB pro systémové operace, kde RoleID=0 (tabulka CSys_operace)
    systemOperationsLevel, //Práva z DB pro systémové operace (tabulka CSys_operace)
    allRolesGlobalValuesAlgorithmicLevel, //Práva z DB pro globální proměnné, kde RoleID=0 a algoritmická úroveň = true
    allRolesGlobalValuesInterfaceLevel, //Práva z DB pro globální proměnné, kde RoleID=0
    globalValuesAlgorithmicLevel, //Práva z DB pro globální proměnné, kde algoritmická úroveň = true
    globalValuesInterfaceLevel, //Práva z DB pro globální proměnné
}

/**
 * Definice práva pro jeden prvek
 */
export interface IItemRights {
    readonly conditionID: string | undefined;
    readonly isNegative: boolean | undefined;
    readonly level: number;
    readonly roleID: string | undefined;
    readonly value: boolean | undefined;
}
type ItemRightElementKeys = "conditionid" | "level" | "roleid" | "isnegative" | "value";
export class ItemRights implements IItemRights {
    public conditionID: string | undefined;
    public isNegative: boolean | undefined;
    public level: number;
    public roleID: string | undefined;
    public value: boolean | undefined;

    constructor(obj: Element);
    constructor(roleID: string, level: number, value: boolean, conditionID?: string, isNegative?: boolean);
    constructor(nodeOrID: Element | string, level?: number, value?: boolean, conditionID?: string, isNegative?: boolean) {
        if (nodeOrID instanceof Element) {
            this.level = DataConverter.toNumber(parseRequiredRightElementAttribute<ItemRightElementKeys>(nodeOrID, "level"));
            this.conditionID = parseRightElementAttribute<ItemRightElementKeys>(nodeOrID, "conditionid");
            this.isNegative = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(nodeOrID, "isnegative"));
            this.roleID = parseRightElementAttribute<ItemRightElementKeys>(nodeOrID, "roleid");
            this.value = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(nodeOrID, "value"));
        } else {
            this.level = level!;
            this.conditionID = conditionID;
            this.isNegative = isNegative;
            this.roleID = nodeOrID;
            this.value = value;
        }
    }
}

/**
 * Základní kolekce různých práv. (Charakteristická homogenitou: každé právo je typu `? ReadonlyArray<IItemRights>`.)
 */
export interface IBaseItemRightsCollection {
    readonly enabled?: ReadonlyArray<IItemRights>;
    readonly postponedRequired?: ReadonlyArray<IItemRights>;
    readonly recommended?: ReadonlyArray<IItemRights>;
    readonly required?: ReadonlyArray<IItemRights>;
    readonly visible?: ReadonlyArray<IItemRights>;
    readonly closeForm?: ReadonlyArray<IItemRights>;
    readonly validateIf?: ReadonlyArray<IItemRights>;
    readonly confirmIf?: ReadonlyArray<IItemRights>;
    readonly useDigitalSign?: ReadonlyArray<IItemRights>;
    readonly useEncryption?: ReadonlyArray<IItemRights>;
    readonly useDecryption?: ReadonlyArray<IItemRights>;
    readonly openAsEnabled?: ReadonlyArray<IItemRights>;
}

/**
 * Kolekce různých práv. (Doplňuje {@link IBaseItemRightsCollection} o další informace - vztažené k již existujícím právům.)
 */
export interface IItemRightsCollection extends IBaseItemRightsCollection {
    /**
     * Pokud není právo {@link IBaseItemRightsCollection.visible} definováno natvrdo a jeho definiciční podmínky nelze vyhodnotit, tak se vezme jako výchozí hodnota.
     */
    readonly showIfDefault: boolean | undefined;
}

/**
 * Interface pro vyhodnocená práva
 */
export interface IHasRights {
    readonly enabled?: boolean;
    readonly postponedRequired?: boolean;
    readonly recommended?: boolean;
    readonly required?: boolean;
    readonly visible?: boolean;
    readonly closeForm?: boolean;
    readonly validateIf?: boolean;
    readonly confirmIf?: boolean;
    readonly useDigitalSign?: boolean;
    readonly useEncryption?: boolean;
    readonly useDecryption?: boolean;
    readonly openAsEnabled?: boolean;
    readonly showIfDefault?: boolean;
    readonly isEditing?: boolean;
    readonly interactive?: boolean;
    readonly enabledVisual?: boolean;
}

// TODO-rites: dokumentace
export class ItemRightsForRightLevels extends Map<RightLevel, ReadonlyArray<IItemRights>> {
    public addItemRights(itemRights: IItemRights): this {
        if (itemRights.level) {
            const rightLevel: RightLevel = itemRights.level;
            const itemsArray = this.get(rightLevel);
            this.set(rightLevel, !itemsArray ? [itemRights] : [...itemsArray, itemRights]);
        }
        return this;
    }

    public flatten(): ReadonlyArray<IItemRights> {
        return this._flatten();
    }

    private _flatten(includingRightLevels?: ReadonlyArray<RightLevel>, excludingRightLevels?: ReadonlyArray<RightLevel>): ReadonlyArray<IItemRights> {
        let resultArray: IItemRights[] = [];

        for (const pair of this) {
            if (includingRightLevels) {
                if (!includingRightLevels.includes(pair[0]))
                    continue;
            } else {
                if (excludingRightLevels && excludingRightLevels.includes(pair[0])) {
                    continue;
                }
            }
            resultArray = resultArray.concat(pair[1]);
        }

        return resultArray;
    }

    public flattenIncludingOnly(...rightLevels: RightLevel[]): ReadonlyArray<IItemRights> {
        return this._flatten(rightLevels);
    }

    public flattenExcluding(...rightLevels: RightLevel[]): ReadonlyArray<IItemRights> {
        return this._flatten(undefined, rightLevels);
    }
}

/**
 * Instance {@link IItemRightsCollection} neobsahující žádná práva.
 */
export const EMPTY_ITEM_RIGHTS_COLLECTION = {} as IItemRightsCollection;
